import { composeRefs } from '@radix-ui/react-compose-refs';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { Settings2, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { Fade } from '../fade';
import { useHorizontalScrollGradient } from '../hooks/use-horizontal-scroll-gradient';
import { useIsTruncated } from '../hooks/use-is-truncated';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../tooltip';
import {
  artifactTabActionsClasses,
  artifactTabDismissButtonClasses,
  artifactTabFadeLeftClasses,
  artifactTabFadeRightClasses,
  artifactTabItemClasses,
  artifactTabItemContentClasses,
  artifactTabItemContentDismissibleClasses,
  artifactTabItemLabelClasses,
  artifactTabItemMenuIconClasses,
  artifactTabItemWrapperClasses,
  artifactTabListClasses,
  artifactTabListInnerClasses,
  artifactTabMenuButtonClasses,
  artifactTabRenameOverlayClasses,
  artifactTabsClasses,
} from './artifact-tabs.styles';
import type {
  ArtifactTabActionsProps,
  ArtifactTabItemProps,
  ArtifactTabListProps,
  ArtifactTabsProps,
} from './artifact-tabs.types';

/** Surface the fade overlays blend into when the consumer doesn't override it. */
const DEFAULT_FADE_BACKGROUND = 'hsl(var(--twc-surface-page))';

/** Internal context for sharing the active tab value with tab items. */
const ArtifactTabsContext = React.createContext<{ activeValue?: string }>({});

/** Hover/focus delay before a truncated-label tooltip opens (ms). */
const TOOLTIP_DELAY_MS = 300;

const ArtifactTabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  ArtifactTabsProps
>(
  (
    { 'data-testid': testId, className, value, defaultValue, onValueChange, ...props },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const activeValue = value ?? internalValue;

    const handleValueChange = React.useCallback(
      (newValue: string) => {
        setInternalValue(newValue);
        onValueChange?.(newValue);
      },
      [onValueChange]
    );

    const ctx = React.useMemo(() => ({ activeValue }), [activeValue]);

    return (
      <ArtifactTabsContext.Provider value={ctx}>
        {/* Provider for the per-tab truncated-label tooltips (see ArtifactTabItem). Kept here
            so the component is self-contained — tooltips work without the consumer supplying a
            provider (Storybook, isolated usage). Nested providers are fine if the app has one. */}
        <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
          <TabsPrimitive.Root
            ref={ref}
            data-testid={testId}
            data-slot="artifact-tabs"
            className={cn(artifactTabsClasses, className)}
            value={value}
            defaultValue={defaultValue}
            onValueChange={handleValueChange}
            {...props}
          />
        </TooltipProvider>
      </ArtifactTabsContext.Provider>
    );
  }
);
ArtifactTabs.displayName = 'ArtifactTabs';

const ArtifactTabList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  ArtifactTabListProps
>(
  (
    {
      'data-testid': testId,
      fadeBackground = DEFAULT_FADE_BACKGROUND,
      className,
      children,
      ...props
    },
    ref
  ) => {
    // The TabsList is the horizontal scroller; track its scroll position so the edge
    // fades only show on the side that has more content. The forwarded ref still points
    // at the list element (composed with the internal scroll ref), so consumers can
    // measure overflow off it.
    const scrollRef = React.useRef<HTMLDivElement>(null);
    // `observeContentChanges` so the edge fades recompute when tabs are added/removed/renamed
    // (the scroller's `scrollWidth` changes while its `clientWidth` doesn't), keeping them in
    // sync with the consumer's content-driven overflow signal.
    const { showLeftGradient, showRightGradient, handleScroll } =
      useHorizontalScrollGradient(scrollRef, { observeContentChanges: true });

    return (
      <div className={artifactTabListClasses} data-testid={testId}>
        <TabsPrimitive.List
          ref={composeRefs(scrollRef, ref)}
          data-slot="artifact-tab-list"
          className={cn(artifactTabListInnerClasses, className)}
          onScroll={handleScroll}
          {...props}
        >
          {children}
        </TabsPrimitive.List>
        <Fade
          side="left"
          background={fadeBackground}
          className={cn(
            artifactTabFadeLeftClasses,
            showLeftGradient ? 'opacity-100' : 'opacity-0'
          )}
        />
        <Fade
          side="right"
          background={fadeBackground}
          className={cn(
            artifactTabFadeRightClasses,
            showRightGradient ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>
    );
  }
);
ArtifactTabList.displayName = 'ArtifactTabList';

const ArtifactTabItem = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  ArtifactTabItemProps
>(
  (
    {
      'data-testid': testId,
      className,
      icon,
      label,
      badge,
      value,
      onDismiss,
      dismissLabel,
      renderMenu,
      menuLabel,
      renameSlot,
      disabledTooltip,
      disabled,
      ...props
    },
    ref
  ) => {
    const { activeValue } = React.useContext(ArtifactTabsContext);
    const dismissible = Boolean(onDismiss);
    const hasMenu = Boolean(renderMenu);
    const isRenaming = Boolean(renameSlot);
    const hasDisabledTooltip = disabled && Boolean(disabledTooltip);
    // The pill grows a positioned wrapper (owning the `artifact-tab-item` slot) the moment it
    // carries any trailing affordance or a rename overlay — not just when it's dismissible.
    const renderWrapper = dismissible || hasMenu || isRenaming || hasDisabledTooltip;
    const isActive = activeValue !== undefined && activeValue === value;
    // The label is clamped to the pill's max-width; only when it's actually truncated do we
    // surface the full text in a tooltip (a tooltip on every tab would be noise).
    const { ref: labelRef, isTruncated } = useIsTruncated<HTMLSpanElement>(label);

    // Selecting a tab must be instant; the pill colour should animate ONLY when the pointer
    // later hovers a tab. The hover-gated colour transition in the styles already makes the
    // *keyboard* path instant (no hover → no transition), but a pointer click lands on a
    // hovered tab — so without this the incoming tab's colour would animate on click.
    // Suppress the transition for the single frame the tab *becomes* active, then restore it
    // next frame (the state has settled, so nothing animates until a genuine hover).
    // setState-during-render lands the suppression on the very first commit; an effect would
    // be a frame late. (The ✕ reveal no longer depends on active state — it follows hover of
    // any tab — so there's nothing on the dismiss button to snap here.)
    const [prevActive, setPrevActive] = React.useState(isActive);
    const [snapOnSelect, setSnapOnSelect] = React.useState(false);
    if (isActive !== prevActive) {
      setPrevActive(isActive);
      // Only the incoming active tab has a colour transition to suppress; skip the no-op
      // frame for the outgoing tab (not hovered → no transition runs anyway).
      if (isActive) {
        setSnapOnSelect(true);
      }
    }
    React.useEffect(() => {
      if (!snapOnSelect) return;
      const raf = requestAnimationFrame(() => setSnapOnSelect(false));
      return () => cancelAnimationFrame(raf);
    }, [snapOnSelect]);

    const trigger = (
      <TabsPrimitive.Trigger
        ref={ref}
        value={value}
        disabled={disabled}
        data-testid={testId}
        // When a wrapper is rendered it owns the item slot (it anchors the trailing buttons and
        // the rename overlay); the trigger must not also carry it.
        data-slot={renderWrapper ? undefined : 'artifact-tab-item'}
        // Snap the colour on the select frame so a click-while-hovering selection is
        // instant (keyboard already snaps — no hover means no colour transition).
        className={cn(
          artifactTabItemClasses,
          className,
          snapOnSelect && '!transition-none'
        )}
        {...props}
        // Detached switcher: the artifact content is a separate page region (the
        // session body), not a co-located `Tabs.Content` under this Root. Radix's
        // Trigger auto-emits `aria-controls` pointing at a panel that never
        // renders — a dangling reference (axe `aria-valid-attr-value`, WCAG 4.1.2).
        // A `tab` is valid without `aria-controls`, so we drop Radix's default:
        // we keep the tablist/tab semantics + roving keyboard and lose only the
        // (already broken) local panel link. A consumer that wires the tab to the
        // real content region via a valid id still wins — its explicit value in
        // `props` is preserved, restoring the full relationship. See DS-1069/DS-1070.
        aria-controls={props['aria-controls']}
      >
        <span
          className={cn(
            artifactTabItemContentClasses,
            // Only the ✕ overlays the trailing edge now, so a single-affordance fade is enough.
            dismissible && artifactTabItemContentDismissibleClasses
          )}
        >
          {/* When the tab has a menu, the leading icon lives in a fixed-size slot that hides on
              hover/focus/open, while the kebab button (a positioned sibling below) reveals on the
              same conditions — so the icon and kebab swap in place. Tabs without a menu render the
              icon as-is. */}
          {hasMenu ? (
            <span aria-hidden className={artifactTabItemMenuIconClasses}>
              {icon}
            </span>
          ) : (
            icon
          )}
          {/* The tooltip wraps the *label* (not the trigger — that owns the active-pill
              `data-state`, which a second Radix trigger would clobber). It's mounted on every
              tab so the measured span never remounts (keeps the ResizeObserver live); the
              content only renders when the label is actually clamped, so non-truncated tabs
              show nothing on hover. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span ref={labelRef} className={artifactTabItemLabelClasses}>
                {label}
              </span>
            </TooltipTrigger>
            {isTruncated && <TooltipContent>{label}</TooltipContent>}
          </Tooltip>
          {badge}
        </span>
      </TabsPrimitive.Trigger>
    );

    if (!renderWrapper) {
      return trigger;
    }

    // The trigger is wrapped in a positioned box that anchors the leading kebab, the trailing ✕,
    // and the rename overlay, and owns the `artifact-tab-item` slot (the reveal, fade-mask, and
    // inactive-lift selectors target it on hover/focus). Buttons sit *outside* the trigger
    // `<button>` to keep the markup valid (no nested buttons).

    // Kebab (⋯): styled here, positioned at the leading edge in place of the tab icon (the
    // content span holds its slot with a spacer), and wrapped by the consumer in its own menu
    // primitive. Always visible; clicking it opens the menu while the rest of the pill still
    // switches tabs.
    const menuButton = hasMenu ? (
      <button
        type="button"
        aria-label={menuLabel}
        // Only the active tab's button is reachable by keyboard; inactive tabs' kebabs are
        // still mouse-clickable (matches the ✕).
        tabIndex={isActive ? 0 : -1}
        data-testid={testId ? `${testId}-menu` : undefined}
        // Align the *glyph* (not the button box) with the static icon it swaps for: the icon sits
        // at the content padding (`start-2.5` = 10px); the 14px glyph is centred in the 20px box,
        // so the box must start 3px earlier (10px − (20−14)/2) for the glyph edges to line up.
        className={cn(artifactTabMenuButtonClasses, 'start-[7px]')}
      >
        <Settings2 />
      </button>
    ) : null;

    const closeButton = dismissible ? (
      <button
        type="button"
        aria-label={dismissLabel}
        tabIndex={isActive ? 0 : -1}
        data-testid={testId ? `${testId}-dismiss` : undefined}
        onClick={(event) => {
          event.stopPropagation();
          onDismiss?.();
        }}
        className={artifactTabDismissButtonClasses}
      >
        <X />
      </button>
    ) : null;

    const wrappedItem = (
      <div data-slot="artifact-tab-item" className={artifactTabItemWrapperClasses}>
        {trigger}
        {/* While renaming, the overlay covers the pill — suppress the kebab + ✕ so they can't
            intercept focus/clicks underneath it. */}
        {!isRenaming && menuButton && renderMenu?.(menuButton)}
        {!isRenaming && closeButton}
        {isRenaming && (
          <div className={artifactTabRenameOverlayClasses}>{renameSlot}</div>
        )}
      </div>
    );

    if (!hasDisabledTooltip) {
      return wrappedItem;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{wrappedItem}</TooltipTrigger>
        <TooltipContent>{disabledTooltip}</TooltipContent>
      </Tooltip>
    );
  }
);
ArtifactTabItem.displayName = 'ArtifactTabItem';

const ArtifactTabActions = React.forwardRef<HTMLDivElement, ArtifactTabActionsProps>(
  ({ 'data-testid': testId, className, ...props }, ref) => (
    <div
      ref={ref}
      data-testid={testId}
      data-slot="artifact-tab-actions"
      className={cn(artifactTabActionsClasses, className)}
      {...props}
    />
  )
);
ArtifactTabActions.displayName = 'ArtifactTabActions';

export { ArtifactTabActions, ArtifactTabItem, ArtifactTabList, ArtifactTabs };
